import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  EuiFieldText,
  EuiButton,
  EuiFormRow,
  EuiSpacer,
  EuiCallOut,
  EuiModal,
  EuiModalHeader,
  EuiModalBody,
  EuiForm,
  EuiModalFooter,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiLoadingSpinner,
  EuiText,
  EuiButtonEmpty,
  EuiIcon,
  EuiBadge,
  EuiCard,
  EuiStepsHorizontal,
  EuiTitle,
  EuiTextArea,
  EuiCheckbox,
  EuiLink,
  EuiHorizontalRule,
  EuiToolTip,
} from "@elastic/eui";
import { useApi } from "src/api";
import {
  AnketaResourcesEnum,
  KeycloakOrganizations,
  useAnketaAuth,
} from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";
import { ApplianceFreshness } from "./model";
import { useWaveAppliances } from "./useWaveAppliances";

interface MergeRequestFormProps {
  targetBranch: string;
  sourceBranch: string;
  onClose: () => void;
  /** Legacy fallback list of affected instance names; the form now fetches the
   *  full per-appliance detail itself, so this is optional. */
  vpcs?: Array<string>;
}

interface MrValidationSummary {
  warnings?: string[];
  included_appliances?: string[];
  excluded_appliances?: string[];
  stale_appliances?: string[];
  missing_prtg?: string[];
}

type FormStep = "form" | "submitting" | "success" | "error";

export const MergeRequestForm: React.FC<MergeRequestFormProps> = ({
  targetBranch,
  sourceBranch,
  onClose,
}) => {
  const { user, keycloak } = useAnketaAuth();
  const { Post, Get } = useApi();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();

  const [reviewerId, setReviewerId] = useState<
    Array<EuiComboBoxOptionOption<any>>
  >([]);
  const [availableReviewers, setAvailableReviewers] = useState<
    Array<EuiComboBoxOptionOption<any>>
  >([]);
  const [isLoadingReviewers, setIsLoadingReviewers] = useState(false);
  const [currentStep, setCurrentStep] = useState<FormStep>("form");
  const [mergeRequestUrl, setMergeRequestUrl] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Create-update flow state: the change summary (entered once, propagated to
  // child waves) and the selected affected appliances.
  const [updateSummary, setUpdateSummary] = useState<string>("");
  // Prefill from the parent wave's update description (the MR into the source
  // wave). Never overwrites text the operator has already typed.
  const [isLoadingInherited, setIsLoadingInherited] = useState(false);
  const [summaryInherited, setSummaryInherited] = useState(false);
  const summaryEdited = useRef(false);
  // Roster first, fork states filled in progressively (see useWaveAppliances).
  const {
    appliances: roster,
    loading: isLoadingAppliances,
    forksPending,
  } = useWaveAppliances(targetBranch);
  const appliances = useMemo<ApplianceFreshness[]>(() => roster ?? [], [roster]);
  const [selectedVpcs, setSelectedVpcs] = useState<Set<string>>(new Set());
  const [validation, setValidation] = useState<MrValidationSummary | null>(null);

  const title = useMemo(
    () => `opsanio Upgrade: ${sourceBranch} => ${targetBranch}`,
    [sourceBranch, targetBranch]
  );

  const assigneeId = user.username;

  // Whether the change summary is required: it is entered once when promoting
  // from the development wave; for later hops it is inherited from the parent
  // wave's MR, so an empty summary is allowed there.
  const summaryRequired = sourceBranch === "dev";

  const deployedAppliances = useMemo(
    () => appliances.filter((a) => a.deployed),
    [appliances]
  );

  const canSubmit = useMemo(
    () =>
      reviewerId.length > 0 &&
      currentStep === "form" &&
      !!keycloak?.token &&
      selectedVpcs.size > 0 &&
      (!summaryRequired || updateSummary.trim().length > 0),
    [
      reviewerId,
      currentStep,
      keycloak?.token,
      selectedVpcs,
      summaryRequired,
      updateSummary,
    ]
  );

  useEffect(() => {
    if (currentStep === "success") {
      const timer = setTimeout(() => {
        onClose();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, onClose]);

  // Load reviewers.
  useEffect(() => {
    if (!keycloak?.token) return;
    setIsLoadingReviewers(true);
    Get<Array<KeycloakOrganizations>>({
      path: `v2/${AnketaResourcesEnum.organizations}/members`,
      responseType: "json",
      token: keycloak.token,
    })
      .then((response) => {
        if (response.data) {
          const org = response.data.find(
            (org) => org.alias === "autonubil-gmbh"
          );
          if (org) {
            const options: Array<EuiComboBoxOptionOption<any>> = org.members
              .filter((member) => member.email !== user.email)
              .map((member) => ({
                label: `${member.username} (${member.firstName} ${member.lastName})`,
                value: member.username,
              }));
            setAvailableReviewers(options);
          }
        }
      })
      .catch((error) => {
        console.error("Failed to fetch reviewers:", error);
        addToast({
          title: translate("anketa.opsanio.waves.reviewers.fetch.error", {
            defaultMessage: "Error loading reviewers",
          }),
          color: "danger",
          text: error.message,
        });
      })
      .finally(() => {
        setIsLoadingReviewers(false);
      });
  }, [keycloak?.token, user.email]);

  // Prefill the change summary from the parent wave. The dev root has no wave
  // MR to inherit from - its summary is entered fresh.
  useEffect(() => {
    if (summaryRequired || !keycloak?.token) return;
    let cancelled = false;
    setIsLoadingInherited(true);
    Get<{ status: number; data: { wave: string; description: string } }>({
      path: `v2/${AnketaResourcesEnum.opsanio}/waves/${encodeURIComponent(
        sourceBranch
      )}/update-description`,
      responseType: "json",
      token: keycloak.token,
    })
      .then((resp) => {
        if (cancelled || !resp.success) return;
        const inherited = resp.data?.data?.description?.trim() ?? "";
        if (inherited && !summaryEdited.current) {
          setUpdateSummary(inherited);
          setSummaryInherited(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingInherited(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceBranch, summaryRequired, keycloak?.token]);

  // Pre-select every deployed appliance once per roster - not again on each
  // fork batch, which would undo the operator's own selection.
  const preselectedFor = useRef<string | null>(null);
  useEffect(() => {
    if (roster === null || preselectedFor.current === targetBranch) return;
    preselectedFor.current = targetBranch;
    setSelectedVpcs(
      new Set(roster.filter((a) => a.deployed).map((a) => a.vpc))
    );
  }, [roster, targetBranch]);

  const toggleAppliance = (vpc: string) => {
    setSelectedVpcs((prev) => {
      const next = new Set(prev);
      if (next.has(vpc)) next.delete(vpc);
      else next.add(vpc);
      return next;
    });
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit) return;

      setCurrentStep("submitting");
      setErrorMessage("");
      setValidation(null);

      const payload = {
        source_branch: sourceBranch,
        target_branch: targetBranch,
        title,
        // The change summary is the propagated "Update description" - the backend
        // wraps it in markers and renders the affected-appliance list itself.
        description: updateSummary,
        assignee_id: assigneeId,
        reviewer_id: reviewerId[0]?.value,
        appliances: Array.from(selectedVpcs),
        user: user.username || "frontend",
      };

      try {
        const { data, error: postError } = await Post({
          path: `v2/${AnketaResourcesEnum.opsanio}/merge-request`,
          value: payload,
          token: keycloak.token,
        });

        if (postError) {
          console.error("Merge request creation failed:", postError);
          // The backend's real reason lives in `service` (the response body
          // `{ error, details }`). `details` on the error itself is the axios
          // request config, and `message` is just "Request failed with status
          // code 409" - neither tells the user what actually went wrong.
          const body = (postError as any)?.service ?? {};
          const backendDetails = Array.isArray(body.details)
            ? body.details.join("\n")
            : body.details;
          const reason =
            backendDetails ||
            body.error ||
            postError.message ||
            translate("anketa.opsanio.waves.merge_request.create.failed", {
              defaultMessage: "Failed to create merge request",
            });
          setErrorMessage(reason);
          setCurrentStep("error");
          addToast({
            title: translate("anketa.opsanio.merge_request.error.title", {
              defaultMessage: "Fehler beim Erstellen des Merge Requests!",
            }),
            color: "danger",
            text: reason,
          });
          return;
        }

        const mrUrl = (data as any)?.merge_request?.web_url || "";
        setMergeRequestUrl(mrUrl);
        setValidation((data as any)?.validation ?? null);
        setCurrentStep("success");
        addToast({
          title: translate("anketa.opsanio.merge_request.created", {
            defaultMessage: "Merge Request erfolgreich erstellt!",
          }),
          color: "success",
          text: mrUrl,
        });
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error("Network error:", err);
        setErrorMessage(errorMsg);
        setCurrentStep("error");
        addToast({
          title: translate("anketa.opsanio.waves.network.error.title", {
            defaultMessage: "Network Error",
          }),
          color: "danger",
          text: errorMsg,
        });
      }
    },
    [
      canSubmit,
      sourceBranch,
      targetBranch,
      title,
      updateSummary,
      assigneeId,
      reviewerId,
      selectedVpcs,
      user.username,
      keycloak.token,
      translate,
    ]
  );

  const renderStepIndicator = () => {
    const steps = [
      {
        title: translate("anketa.opsanio.waves.merge_request.step.configure", {
          defaultMessage: "Configure",
        }),
        isSelected: currentStep === "form",
        isComplete: currentStep !== "form",
        onClick: () => {},
      },
      {
        title: translate("anketa.opsanio.waves.merge_request.step.creating", {
          defaultMessage: "Creating",
        }),
        isSelected: currentStep === "submitting",
        isComplete: currentStep === "success" || currentStep === "error",
        onClick: () => {},
      },
      {
        title:
          currentStep === "error"
            ? translate("anketa.opsanio.waves.merge_request.step.failed", {
                defaultMessage: "Failed",
              })
            : translate("anketa.opsanio.waves.merge_request.step.complete", {
                defaultMessage: "Complete",
              }),
        isSelected: currentStep === "success" || currentStep === "error",
        isComplete: currentStep === "success",
        onClick: () => {},
      },
    ];
    return <EuiStepsHorizontal steps={steps} />;
  };

  const renderApplianceRow = (a: ApplianceFreshness) => {
    const checked = selectedVpcs.has(a.vpc);
    return (
      <EuiPanel
        key={a.vpc}
        paddingSize="s"
        hasBorder
        color={a.deployed ? "transparent" : "subdued"}
        style={{ marginBottom: 8, opacity: a.deployed ? 1 : 0.6 }}
      >
        <EuiFlexGroup gutterSize="s" alignItems="flexStart">
          <EuiFlexItem grow={false}>
            <EuiCheckbox
              id={`appliance-${a.vpc}`}
              checked={checked && a.deployed}
              disabled={!a.deployed}
              onChange={() => toggleAppliance(a.vpc)}
            />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiFlexGroup gutterSize="s" alignItems="center" wrap>
              <EuiFlexItem grow={false}>
                <EuiText size="s">
                  <strong>{a.vpc}</strong>
                </EuiText>
              </EuiFlexItem>
              {!a.deployed && (
                <EuiFlexItem grow={false}>
                  <EuiBadge color="default">
                    {translate("anketa.opsanio.waves.appliance.not_deployed", {
                      defaultMessage: "not deployed",
                    })}
                  </EuiBadge>
                </EuiFlexItem>
              )}
              {a.manageViaCapi && (
                <EuiFlexItem grow={false}>
                  <EuiToolTip
                    content={translate(
                      "anketa.opsanio.waves.appliance.capi.tooltip",
                      {
                        defaultMessage:
                          "Cluster API owns this cluster: no 200-k3-server / 210-k3s Terraform projects, so no fork check and no deployment pipelines",
                      }
                    )}
                  >
                    <EuiBadge color="hollow" iconType="node">
                      {translate("anketa.opsanio.waves.appliance.capi", {
                        defaultMessage: "CAPI-managed",
                      })}
                    </EuiBadge>
                  </EuiToolTip>
                </EuiFlexItem>
              )}
              {a.deployed && !a.manageViaCapi && a.forksChecked === false && (
                <EuiFlexItem grow={false}>
                  <EuiBadge color="hollow">
                    <EuiLoadingSpinner size="s" />{" "}
                    {translate("anketa.opsanio.waves.appliance.fork_checking", {
                      defaultMessage: "checking forks",
                    })}
                  </EuiBadge>
                </EuiFlexItem>
              )}
              {a.deployed &&
                !a.manageViaCapi &&
                a.forksChecked !== false &&
                !a.allFresh && (
                <EuiFlexItem grow={false}>
                  <EuiToolTip
                    content={translate(
                      "anketa.opsanio.waves.appliance.stale_fork.tooltip",
                      {
                        defaultMessage:
                          "One or more stage forks are behind their golden upstream",
                      }
                    )}
                  >
                    <EuiBadge color="warning" iconType="alert">
                      {translate("anketa.opsanio.waves.appliance.stale_fork", {
                        defaultMessage: "stale fork",
                      })}
                    </EuiBadge>
                  </EuiToolTip>
                </EuiFlexItem>
              )}
              {a.prtg.configured && a.prtg.missing && (
                <EuiFlexItem grow={false}>
                  <EuiBadge color="warning" iconType="alert">
                    {translate("anketa.opsanio.waves.appliance.prtg_missing", {
                      defaultMessage: "PRTG missing",
                    })}
                  </EuiBadge>
                </EuiFlexItem>
              )}
            </EuiFlexGroup>

            <EuiSpacer size="xs" />

            <EuiFlexGroup gutterSize="m" wrap responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiText size="xs" color="subdued">
                  <EuiIcon type="branch" size="s" />{" "}
                  {a.manageViaCapi
                    ? translate("anketa.opsanio.waves.appliance.capi_no_forks", {
                        defaultMessage: "CAPI - no Terraform forks",
                      })
                    : a.forkLinks.length > 0
                    ? a.forkLinks.map((f, i) => (
                        <React.Fragment key={f.leaf}>
                          {i > 0 && ", "}
                          <EuiLink href={f.url} target="_blank">
                            {f.leaf}
                          </EuiLink>
                        </React.Fragment>
                      ))
                    : translate("anketa.opsanio.waves.appliance.no_forks", {
                        defaultMessage: "no forks",
                      })}
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiText size="xs" color="subdued">
                  <EuiIcon type="visGauge" size="s" />{" "}
                  {a.prtg.link ? (
                    <EuiLink href={a.prtg.link} target="_blank">
                      PRTG
                    </EuiLink>
                  ) : a.prtg.configured ? (
                    translate("anketa.opsanio.waves.appliance.no_probes", {
                      defaultMessage: "no probes",
                    })
                  ) : (
                    translate("anketa.opsanio.waves.appliance.prtg_na", {
                      defaultMessage: "PRTG n/a",
                    })
                  )}
                </EuiText>
              </EuiFlexItem>
              {(a.pangolinLinks ?? []).map((p) => (
                <EuiFlexItem grow={false} key={p.app}>
                  <EuiText size="xs" color="subdued">
                    <EuiIcon type="globe" size="s" />{" "}
                    <EuiLink href={p.url} target="_blank">
                      {p.app}
                    </EuiLink>
                  </EuiText>
                </EuiFlexItem>
              ))}
              <EuiFlexItem grow={false}>
                <EuiText size="xs" color="subdued">
                  <EuiIcon type="email" size="s" />{" "}
                  {a.customerContacts.join(", ")}
                  {a.contactsDefaulted &&
                    " " +
                      translate(
                        "anketa.opsanio.waves.appliance.contacts_default",
                        { defaultMessage: "(default)" }
                      )}
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiPanel>
    );
  };

  const renderSuccessState = () => (
    <EuiPanel color="success" hasShadow={false}>
      <EuiFlexGroup direction="column" alignItems="center" gutterSize="s">
        <EuiFlexItem grow={false}>
          <EuiIcon type="checkInCircleFilled" size="xxl" color="success" />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiText>
            <h3>
              {translate("anketa.opsanio.waves.merge_request.created.title", {
                defaultMessage: "Merge Request Created",
              })}
            </h3>
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            href={mergeRequestUrl}
            target="_blank"
            iconType="popout"
            fill
            color="success"
          >
            {translate("anketa.opsanio.documentation.open.gitlab", {
              defaultMessage: "Open in GitLab",
            })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>

      {validation && (
        <>
          <EuiHorizontalRule margin="m" />
          <EuiText size="s">
            <p>
              <strong>
                {translate(
                  "anketa.opsanio.waves.validation.included",
                  { defaultMessage: "Included:" }
                )}
              </strong>{" "}
              {validation.included_appliances?.join(", ") ||
                translate("anketa.opsanio.waves.validation.none", {
                  defaultMessage: "none",
                })}
            </p>
            {validation.excluded_appliances &&
              validation.excluded_appliances.length > 0 && (
                <p>
                  <strong>
                    {translate(
                      "anketa.opsanio.waves.validation.excluded",
                      { defaultMessage: "Excluded:" }
                    )}
                  </strong>{" "}
                  {validation.excluded_appliances.join(", ")}
                </p>
              )}
            {validation.stale_appliances &&
              validation.stale_appliances.length > 0 && (
                <p>
                  <strong>
                    {translate(
                      "anketa.opsanio.waves.validation.stale_forks",
                      { defaultMessage: "Stale forks:" }
                    )}
                  </strong>{" "}
                  {validation.stale_appliances.join(", ")}
                </p>
              )}
            {validation.missing_prtg &&
              validation.missing_prtg.length > 0 && (
                <p>
                  <strong>
                    {translate(
                      "anketa.opsanio.waves.validation.missing_prtg",
                      { defaultMessage: "Missing PRTG:" }
                    )}
                  </strong>{" "}
                  {validation.missing_prtg.join(", ")}
                </p>
              )}
          </EuiText>
          {validation.warnings && validation.warnings.length > 0 && (
            <EuiCallOut
              size="s"
              color="warning"
              title={translate("anketa.opsanio.waves.validation.warnings", {
                defaultMessage: "Warnings",
              })}
              iconType="alert"
            >
              <ul>
                {validation.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </EuiCallOut>
          )}
        </>
      )}
    </EuiPanel>
  );

  const renderErrorState = () => (
    <EuiCallOut
      title={translate("anketa.opsanio.waves.merge_request.create.failed", {
        defaultMessage: "Failed to create merge request",
      })}
      color="danger"
      iconType="alert"
    >
      <p style={{ whiteSpace: "pre-wrap" }}>{errorMessage}</p>
    </EuiCallOut>
  );

  const renderSubmittingState = () => (
    <EuiPanel color="subdued" hasShadow={false} style={{ textAlign: "center" }}>
      <EuiSpacer size="l" />
      <EuiLoadingSpinner size="xl" />
      <EuiSpacer size="m" />
      <EuiText>
        <h3>
          {translate("anketa.opsanio.waves.merge_request.creating", {
            defaultMessage: "Creating Merge Request...",
          })}
        </h3>
      </EuiText>
      <EuiSpacer size="l" />
    </EuiPanel>
  );

  const renderFormState = () => (
    <EuiForm component="form" onSubmit={handleSubmit}>
      <EuiFlexGroup gutterSize="m">
        <EuiFlexItem>
          <EuiCard
            title={translate("anketa.opsanio.waves.merge_request.source", {
              defaultMessage: "Source",
            })}
            description={
              <EuiBadge color="primary">{sourceBranch}</EuiBadge>
            }
            icon={<EuiIcon type="branch" size="l" />}
            titleSize="xs"
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false} style={{ alignSelf: "center" }}>
          <EuiIcon type="sortRight" size="l" />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiCard
            title={translate("anketa.opsanio.waves.merge_request.target", {
              defaultMessage: "Target",
            })}
            description={
              <EuiBadge color="success">{targetBranch}</EuiBadge>
            }
            icon={<EuiIcon type="merge" size="l" />}
            titleSize="xs"
          />
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="l" />

      <EuiFormRow
        label={translate("anketa.opsanio.waves.form.change_summary.label", {
          defaultMessage: "Change summary",
        })}
        helpText={
          summaryRequired
            ? translate(
                "anketa.opsanio.waves.form.change_summary.help.required",
                {
                  defaultMessage:
                    "Markdown supported. Entered once in the Development wave; propagated verbatim to all child waves and rendered into the customer update email.",
                }
              )
            : isLoadingInherited
            ? translate(
                "anketa.opsanio.waves.form.change_summary.help.loading",
                {
                  defaultMessage:
                    "Loading the change summary of the parent wave ({wave})...",
                  values: { wave: sourceBranch },
                }
              )
            : summaryInherited
            ? translate(
                "anketa.opsanio.waves.form.change_summary.help.prefilled",
                {
                  defaultMessage:
                    "Markdown supported. Prefilled from the merge request of the parent wave ({wave}); edit if needed.",
                  values: { wave: sourceBranch },
                }
              )
            : translate(
                "anketa.opsanio.waves.form.change_summary.help.inherited",
                {
                  defaultMessage:
                    "Markdown supported. Inherited from the parent wave's update when left blank.",
                }
              )
        }
        isInvalid={summaryRequired && updateSummary.trim().length === 0}
        error={translate("anketa.opsanio.waves.form.change_summary.error", {
          defaultMessage:
            "A change summary is required when creating from the Development wave",
        })}
        fullWidth
      >
        <EuiTextArea
          placeholder={translate(
            "anketa.opsanio.waves.form.change_summary.placeholder",
            { defaultMessage: "What does this update change?" }
          )}
          value={updateSummary}
          onChange={(e) => {
            summaryEdited.current = true;
            setUpdateSummary(e.target.value);
          }}
          fullWidth
          rows={4}
        />
      </EuiFormRow>

      <EuiFormRow
        label={translate("anketa.opsanio.waves.form.title.label", {
          defaultMessage: "Merge Request Title",
        })}
        fullWidth
      >
        <EuiFieldText value={title} disabled readOnly fullWidth icon="document" />
      </EuiFormRow>

      <EuiFormRow
        label={translate("anketa.opsanio.waves.form.assignee.label", {
          defaultMessage: "Assignee",
        })}
        fullWidth
      >
        <EuiFieldText value={assigneeId} disabled readOnly fullWidth icon="user" />
      </EuiFormRow>

      <EuiFormRow
        label={translate("anketa.medocino.support.reviewer.label", {
          defaultMessage: "Reviewer",
        })}
        helpText={translate("anketa.opsanio.waves.form.reviewer.help", {
          defaultMessage: "Select a team member to review this merge request",
        })}
        isInvalid={!isLoadingReviewers && reviewerId.length === 0}
        error={
          !isLoadingReviewers && reviewerId.length === 0
            ? translate("anketa.opsanio.waves.form.reviewer.error", {
                defaultMessage: "Please select a reviewer",
              })
            : undefined
        }
        fullWidth
      >
        {isLoadingReviewers ? (
          <EuiLoadingSpinner size="m" />
        ) : (
          <EuiComboBox
            placeholder={translate(
              "anketa.opsanio.waves.form.reviewer.placeholder",
              { defaultMessage: "Choose a reviewer" }
            )}
            options={availableReviewers}
            singleSelection={{ asPlainText: true }}
            selectedOptions={reviewerId}
            onChange={setReviewerId}
            isClearable={true}
            fullWidth
            prepend={<EuiIcon type="userAvatar" />}
          />
        )}
      </EuiFormRow>

      <EuiSpacer size="m" />

      <EuiFormRow
        label={translate("anketa.opsanio.waves.form.appliances.label", {
          values: {
            selected: selectedVpcs.size,
            total: deployedAppliances.length,
          },
          defaultMessage: "Affected appliances ({{selected}}/{{total}} selected)",
        })}
        isInvalid={selectedVpcs.size === 0}
        error={translate("anketa.opsanio.waves.form.appliances.error", {
          defaultMessage: "Select at least one appliance",
        })}
        fullWidth
      >
        <div>
          {isLoadingAppliances ? (
            <EuiFlexGroup gutterSize="s" alignItems="center">
              <EuiFlexItem grow={false}>
                <EuiLoadingSpinner size="m" />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="s" color="subdued">
                  {translate("anketa.opsanio.waves.form.appliances.loading", {
                    defaultMessage: "Loading appliances...",
                  })}
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          ) : appliances.length === 0 ? (
            <EuiText size="s" color="subdued">
              {translate("anketa.opsanio.waves.form.appliances.empty", {
                defaultMessage: "No appliances found on this wave.",
              })}
            </EuiText>
          ) : (
            <>
              {forksPending > 0 && (
                <EuiText size="xs" color="subdued">
                  {translate(
                    "anketa.opsanio.waves.form.appliances.forks_pending",
                    {
                      values: { count: forksPending },
                      defaultMessage: "Checking forks... ({{count}} left)",
                    }
                  )}
                </EuiText>
              )}
              {appliances.map(renderApplianceRow)}
            </>
          )}
        </div>
      </EuiFormRow>

      <EuiSpacer size="m" />

      <EuiCallOut
        size="s"
        title={translate("anketa.opsanio.waves.form.preview.title", {
          defaultMessage: "Preview",
        })}
        iconType="eye"
        color="primary"
      >
        <EuiText size="s">
          <p>
            {translate("anketa.opsanio.waves.form.preview.intro", {
              defaultMessage:
                "This creates a GitLab merge request promoting",
            })}{" "}
            <EuiBadge color="hollow">{sourceBranch}</EuiBadge> →{" "}
            <EuiBadge color="hollow">{targetBranch}</EuiBadge>{" "}
            {translate("anketa.opsanio.waves.form.preview.outro", {
              values: { count: selectedVpcs.size },
              defaultMessage:
                "for {{count}} appliance(s). Non-deployed appliances are excluded automatically.",
            })}
          </p>
        </EuiText>
      </EuiCallOut>
    </EuiForm>
  );

  return (
    <EuiModal onClose={onClose} maxWidth={760} style={{ width: 760 }}>
      <EuiModalHeader>
        <EuiFlexGroup direction="column" gutterSize="s">
          <EuiFlexItem>
            <EuiTitle>
              <h2>
                <EuiIcon type="merge" size="l" style={{ marginRight: 8 }} />
                {translate("anketa.opsanio.waves.create_update", {
                  defaultMessage: "Create update",
                })}
              </h2>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem>{renderStepIndicator()}</EuiFlexItem>
        </EuiFlexGroup>
      </EuiModalHeader>

      <EuiModalBody>
        {currentStep === "form" && renderFormState()}
        {currentStep === "submitting" && renderSubmittingState()}
        {currentStep === "success" && renderSuccessState()}
        {currentStep === "error" && renderErrorState()}
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty
          onClick={onClose}
          disabled={currentStep === "submitting"}
        >
          {currentStep === "success"
            ? translate("anketa.action.close", { defaultMessage: "Close" })
            : translate("anketa.opsanio.modal.cancel", {
                defaultMessage: "Cancel",
              })}
        </EuiButtonEmpty>
        {currentStep === "form" && (
          <EuiButton
            type="submit"
            onClick={handleSubmit}
            fill
            isDisabled={!canSubmit}
            iconType="merge"
          >
            {translate("anketa.opsanio.waves.merge_request.submit", {
              defaultMessage: "Create Merge Request",
            })}
          </EuiButton>
        )}
        {currentStep === "error" && (
          <EuiButton
            onClick={() => setCurrentStep("form")}
            fill
            color="primary"
            iconType="refresh"
          >
            {translate("anketa.opsanio.waves.try_again", {
              defaultMessage: "Try Again",
            })}
          </EuiButton>
        )}
      </EuiModalFooter>
    </EuiModal>
  );
};
